"use client";

import { useEffect, useState } from "react";
import {
  DispensaryLocations,
  Header,
  SponsoredBanners,
  SponsoredBannersProvider,
} from "@/components";
import type { Store } from "@/lib/types/stores";
import CategorySection from "@/components/sections/CategorySection";
import CommunitySection from "@/components/sections/CommunitySection";
import CtaBannerSection from "@/components/sections/CtaBannerSection";
import DankLifestyleSection from "@/components/sections/DankLifestyleSection";
import BlogsSection from "@/components/sections/BlogsSection";
import FAQSection from "@/components/sections/FAQSection";
import JoinTheFamSection from "@/components/sections/JoinTheFamSection";
import ProductShowcaseSection from "@/components/sections/ProductShowcaseSection";
import ReviewsSection from "@/components/sections/ReviewsSection";
import { PageShowcasesProvider } from "@/components/providers/PageShowcasesProvider";
import {
  WhyDankCommunitySection,
  WhyDankFinestSection,
  WhyDankHeroSection,
  WhyDankSeoBlocksSection,
} from "@/components/sections/why-dank";
import { DEFAULT_CATEGORY_TILES as categories } from "@/lib/category-tiles";
import {
  WHY_DANK_COMMUNITY_SPONSORED_POSITION,
  WHY_DANK_FAQ_SPONSORED_POSITION,
  WHY_DANK_MERCH_SPONSORED_POSITION,
} from "@/lib/sponsored-positions";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";


export default function WhyDankPage() {
  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stores`, { cache: "no-store" });
        if (res.ok) {
          const data = await res.json();
          if (data?.stores) setStores(data.stores);
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  return (
    <SponsoredBannersProvider>
      <PageShowcasesProvider endpoint="/api/why-dank/product-showcases">
        <div className="min-h-screen w-full min-w-0 max-w-none">
          <Header stores={stores} base={API_BASE} />

          <main className="home-page w-full max-w-none pb-16 py-4 sm:py-6">
            <div className="w-full max-w-none space-y-16 px-4 sm:space-y-36 sm:px-8 [&>section]:!px-0">
              <WhyDankHeroSection />
              <WhyDankFinestSection />

              <WhyDankCommunitySection />

              <CommunitySection
                pageSlug="why-dank"
                sponsoredPosition={WHY_DANK_COMMUNITY_SPONSORED_POSITION}
              />

              <ProductShowcaseSection slotKey="productSelection" className="!px-0" />

              <DispensaryLocations base={API_BASE} />

              <CtaBannerSection />

              <CategorySection categories={categories} contentClassName="!px-0" />

              <div className="flex w-full max-w-none flex-col gap-6 lg:gap-10 [&>section]:!px-0">
                <DankLifestyleSection apiPath="/api/homepage/merch" />

                <section className="w-full max-w-none" aria-label="Sponsored content">
                  <SponsoredBanners
                    position={WHY_DANK_MERCH_SPONSORED_POSITION}
                    limit={1}
                    className="w-full"
                  />
                </section>
              </div>

              <JoinTheFamSection />
              <ReviewsSection />

              <BlogsSection />

              <FAQSection sponsoredPosition={WHY_DANK_FAQ_SPONSORED_POSITION} />

              <WhyDankSeoBlocksSection />
            </div>
          </main>
        </div>
      </PageShowcasesProvider>
    </SponsoredBannersProvider>
  );
}
